import React, { useCallback, useState } from 'react'
import { ActivityIndicator, Modal, Pressable, StyleSheet, View } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

import { DS } from '@/design-system'
import { useStore } from '@/models'
import { translate } from '../../i18n'
import { palette } from '../../theme/palette'
import { useGameContext } from './GameContext'
import { useGameState } from './useGameState'
import { useSpectatorStatus } from './useSpectatorStatus'

type SeatChoice = 'teacher' | 'player'

const SOLO_OR_TOURNAMENT_MODES = ['practice', 'daily10', 'mtt']

/** Clears the game header (chat / key / menu row) so the card sits under it, above bottom sheets. */
const HEADER_OFFSET = 72

/**
 * Seat picker for teachers: teacher seat (middle of the table) or player seat.
 *
 * Shown when a teacher lands as an observer (teachers always join as observers — see `joinGame`).
 * The first teacher to pick the teacher seat is sat in the middle of the table; if it is already
 * taken, the teacher simply stays on as an invisible observer.
 *
 * Self-contained: RN Modal anchored under the game header, clear of bottom sheets (e.g. WaitingForPlayersSheet).
 */
export const TeacherSeatChoiceModal = observer(() => {
  const {
    session: { isTeacher, user }
  } = useStore()
  const { gameId, userId } = useGameContext()
  const { game, players, teacher, isTeacherSeated, myPlayer } = useGameState()
  const { isSpectator } = useSpectatorStatus(gameId, userId)
  const insets = useSafeAreaInsets()

  const joinAsTeacher = useMutation(api.games.teacherSeat.joinAsTeacher)
  const switchToTeacherSeat = useMutation(api.games.teacherSeat.switchToTeacherSeat)
  const switchToPlayerSeat = useMutation(api.games.teacherSeat.switchToPlayerSeat)

  const [dismissed, setDismissed] = useState(false)
  const [pending, setPending] = useState<SeatChoice | null>(null)
  const [error, setError] = useState<string | null>(null)

  const userName = user?.name ?? 'Teacher'
  const teacherSeatTakenBy = teacher && teacher.userId !== userId ? teacher.name : null
  const isTableFull = Boolean(game && players.length >= game.maxPlayers)

  const close = useCallback(() => setDismissed(true), [])

  const handleChoice = useCallback(
    async (choice: SeatChoice) => {
      if (!game || pending) return

      setPending(choice)
      setError(null)
      try {
        if (choice === 'teacher') {
          // Atomic seat→teacher switch so leaving a player seat never auto-ends a bots-only table.
          // If another teacher already holds the seat, this leaves them on as an invisible observer.
          if (myPlayer) {
            await switchToTeacherSeat({ gameId: game._id, userId, name: userName })
          } else {
            await joinAsTeacher({ gameId: game._id, userId, name: userName })
          }
        } else {
          await switchToPlayerSeat({
            gameId: game._id,
            userId,
            name: userName,
            mongoUserId: user?._id?.toString()
          })
        }
        close()
      } catch (err) {
        console.error('[TeacherSeatChoiceModal] Failed to change seat:', err)
        setError(translate('teacherSeatChoice.genericError'))
      } finally {
        setPending(null)
      }
    },
    [
      game,
      pending,
      close,
      myPlayer,
      joinAsTeacher,
      switchToTeacherSeat,
      switchToPlayerSeat,
      userId,
      userName,
      user
    ]
  )

  const isEligibleGame =
    game && !game.tournamentId && !SOLO_OR_TOURNAMENT_MODES.includes(game.mode) && game.state !== 'finished'
  const landedAsObserver = isSpectator && !isTeacherSeated
  if (!isTeacher || !isEligibleGame || dismissed || !landedAsObserver) return null

  const options: { choice: SeatChoice; title: string; description: string; disabled: boolean }[] = [
    {
      choice: 'teacher',
      title: translate('teacherSeatChoice.teacherSeat'),
      description: teacherSeatTakenBy
        ? translate('teacherSeatChoice.teacherSeatTakenBy', { name: teacherSeatTakenBy })
        : translate('teacherSeatChoice.teacherSeatDescription'),
      disabled: false
    },
    {
      choice: 'player',
      title: translate('teacherSeatChoice.playerSeat'),
      description: isTableFull
        ? translate('teacherSeatChoice.tableFull')
        : translate('teacherSeatChoice.playerSeatDescription'),
      disabled: isTableFull
    }
  ]

  // Rendered in an RN Modal and anchored to the top of the screen so it never collides with
  // bottom sheets (e.g. WaitingForPlayersSheet), which are portaled above the game tree.
  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent onRequestClose={close}>
      <View style={[styles.overlay, { paddingTop: insets.top + HEADER_OFFSET }]}>
        <View style={styles.backdrop} />
        <View style={styles.modalContainer}>
          <DS.Text variant="Heading 2/Medium" textAlign="center">
            {translate('teacherSeatChoice.title')}
          </DS.Text>
          <DS.Text variant="Body Small/Regular" textAlign="center" color="Brand/Dark Grey">
            {translate('teacherSeatChoice.subtitle')}
          </DS.Text>

          <View style={styles.options}>
            {options.map(({ choice, title, description, disabled }) => {
              const isDisabled = disabled || pending !== null
              return (
                <Pressable
                  key={choice}
                  onPress={() => handleChoice(choice)}
                  disabled={isDisabled}
                  accessibilityRole="button"
                  accessibilityState={{ disabled: isDisabled, busy: pending === choice }}
                  style={({ pressed }) => [
                    styles.option,
                    pressed && styles.optionPressed,
                    disabled && styles.optionDisabled
                  ]}
                >
                  <View style={styles.optionText}>
                    <DS.Text variant="Body Medium/Medium" color="Global/Black">
                      {title}
                    </DS.Text>
                    <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
                      {description}
                    </DS.Text>
                  </View>
                  {pending === choice ? (
                    <ActivityIndicator size="small" color={DS.palette['Brand/Maroon']} />
                  ) : (
                    <DS.Icon name="Arrow---right" size={20} color="Global/Black" />
                  )}
                </Pressable>
              )
            })}
          </View>

          {error && (
            <DS.Text variant="Body Small/Regular" textAlign="center" color="Brand/Orange">
              {error}
            </DS.Text>
          )}
        </View>
      </View>
    </Modal>
  )
})

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.blackWithOpacity.twenty
  },
  modalContainer: {
    backgroundColor: DS.palette['Brand/Cream'],
    marginHorizontal: 24,
    paddingHorizontal: 24,
    paddingVertical: 24,
    borderRadius: 20,
    maxWidth: 400,
    width: '85%',
    gap: 12
  },
  options: {
    gap: 8,
    marginTop: 8
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    backgroundColor: DS.palette['Global/White']
  },
  optionPressed: {
    opacity: 0.7
  },
  optionDisabled: {
    opacity: 0.5
  },
  optionText: {
    flex: 1,
    gap: 2
  }
})
